import React, { useContext, useMemo, useState } from 'react'
import { isTickmillCompanyEUType } from '@/api/Clients/Types/TickmillCompanyTypes'
import { FormSelectField } from '@/components/Form/FormField'
import { ClientDetailedDto } from 'api/Clients/Types/ClientDetailedDto'
import { UpdateClientStatus } from 'api/Clients/Types/UpdateClientStatus'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { AuthSessionContext } from 'utils/AuthContext'
import { getEnumEntries } from 'utils/getEnumEntries'
import { hasPermission } from 'utils/permissionsValidator'

import { ClientNavigation, ClientTab } from '../Navigation/ClientNavigation'
import ClientClassificationTestPage from './ClientTests/ClientClassificationTestPage'
import ClientCorporateTestPage from './ClientTests/ClientCorporateTestPage'
import ClientEconomicProfileTestPage from './ClientTests/ClientEconomicProfileTestPage'
import ClientIBTestPage from './ClientTests/ClientIBTestPage'
import ClientKYCTestPage from './ClientTests/ClientKYCTestPage'
import ClientTestPage from './ClientTests/ClientTestPage'
import W8BeneficialPage from './ClientTests/W8BeneficialPage'

export enum TestsViewType {
  Tests,
  IBInfo,
  Classification,
  KYC,
  W8BENForm,
  CorporateTest,
  EconomicProfile,
}

interface Props {
  data: ClientDetailedDto
  updateClientStatus(values: UpdateClientStatus): void
  forceRefresh(): void
}
interface ClientDetailsTestProps extends Props {
  defaultTestViewType: TestsViewType
}

const ClientDetailsTest: React.FC<ClientDetailsTestProps> = ({
  data,
  updateClientStatus,
  forceRefresh,
  defaultTestViewType,
}) => {
  const [testViewType, setTestSelectedViewType] = useState<TestsViewType>(defaultTestViewType)

  const [auth] = useContext(AuthSessionContext)

  const permissions = auth?.permissions || []

  const handleTestViewType = (value: string | number) => {
    setTestSelectedViewType(Number.parseInt(value.toString(), 10))
  }

  const formatEnumName = (name: string): string => {
    if (name === 'CorporateTest') return 'Corporate Test'
    if (name === 'EconomicProfile') return 'Economic Profile'
    return name
  }

  const testOptions = useMemo(
    () =>
      getEnumEntries(TestsViewType)
        .filter(
          ([, viewType]) =>
            (viewType === TestsViewType.Tests &&
              hasPermission(permissions, PermissionsEnum.CanViewClientTests)) ||
            (viewType === TestsViewType.IBInfo &&
              hasPermission(permissions, PermissionsEnum.CanViewClientIBInformation)) ||
            (viewType === TestsViewType.Classification &&
              hasPermission(permissions, PermissionsEnum.CanViewClientClassification)) ||
            (viewType === TestsViewType.KYC &&
              hasPermission(permissions, PermissionsEnum.CanViewClientKycUpdateTests)) ||
            viewType === TestsViewType.W8BENForm ||
            viewType === TestsViewType.CorporateTest ||
            (viewType === TestsViewType.EconomicProfile &&
              hasPermission(permissions, PermissionsEnum.CanViewEconomicProfileTests) &&
              isTickmillCompanyEUType(data.tickmillCompany.id))
        )
        .map(([name, id]) => ({ name: formatEnumName(name), id })),
    []
  )

  return (
    <div>
      <ClientNavigation
        isEdit={false}
        data={data}
        tab={ClientTab.TESTS}
        updateClientStatus={updateClientStatus}
      />
      <FormSelectField
        className='max-w-xs py-2'
        value={testViewType}
        onChange={handleTestViewType}
        options={testOptions}
        label='Test Type'
      />
      <TestsViewTypePages
        data={data}
        updateClientStatus={updateClientStatus}
        forceRefresh={forceRefresh}
        viewType={testViewType}
      />
    </div>
  )
}

interface TestsViewTypePagesProps {
  data: ClientDetailedDto
  updateClientStatus(values: UpdateClientStatus): void
  forceRefresh(): void
  viewType: number
}

const TestsViewTypePages: React.FC<TestsViewTypePagesProps> = (props) => {
  const { data, updateClientStatus, forceRefresh, viewType } = props

  const [auth] = useContext(AuthSessionContext)

  const permissions = auth?.permissions || []

  if (
    viewType === TestsViewType.Tests &&
    hasPermission(permissions, PermissionsEnum.CanViewClientTests)
  ) {
    return <ClientTestPage data={data} />
  }

  if (
    viewType === TestsViewType.IBInfo &&
    hasPermission(permissions, PermissionsEnum.CanViewClientIBInformation)
  ) {
    return <ClientIBTestPage data={data} />
  }

  if (
    viewType === TestsViewType.Classification &&
    hasPermission(permissions, PermissionsEnum.CanViewClientClassification)
  ) {
    return <ClientClassificationTestPage data={data} forceRefresh={forceRefresh} />
  }

  if (
    viewType === TestsViewType.KYC &&
    hasPermission(permissions, PermissionsEnum.CanViewClientKycUpdateTests)
  ) {
    return <ClientKYCTestPage data={data} />
  }

  if (viewType === TestsViewType.W8BENForm) {
    return (
      <W8BeneficialPage
        data={data}
        forceRefresh={forceRefresh}
        updateClientStatus={updateClientStatus}
      />
    )
  }
  if (viewType === TestsViewType.CorporateTest) {
    return <ClientCorporateTestPage data={data} />
  }

  if (
    viewType === TestsViewType.EconomicProfile &&
    hasPermission(permissions, PermissionsEnum.CanViewEconomicProfileTests)
  ) {
    return <ClientEconomicProfileTestPage data={data} forceRefresh={forceRefresh} />
  }

  return null
}

export default ClientDetailsTest
